// Sensitivity / FOV conversion helpers for the in-game "convert from another
// game" panel. The game itself is Quake/Warfork-style: `angle = dx *
// sensitivity * m_yaw` with m_yaw = m_pitch = 0.022 degrees per count, so the
// physical turn rate (cm/360) is the only thing worth matching.

import type { SensFovType } from "./sens-data";

/** Quake/Warfork yaw+pitch: degrees per mouse count at sensitivity 1.0. */
export const OUR_YAW = 0.022;

/** cm of mouse travel for a full 360° turn. */
export function sensToCm360(sens: number, dpi: number, yaw: number): number {
  return 914.4 / (sens * dpi * yaw);
}

/** Sensitivity that turns 360° in `cm360` centimetres at `dpi`. */
export function cm360ToSens(cm360: number, dpi: number, yaw: number): number {
  return 914.4 / (cm360 * dpi * yaw);
}

/** Same physical 360° in webrace's yaw. */
export function toOurSensitivity(sourceSens: number, sourceYaw: number): number {
  return (sourceSens * sourceYaw) / OUR_YAW;
}

/** webrace sensitivity for a source game's cm/360 (DPI/yaw independent of ours). */
export function ourSensitivityFromCm360(cm360: number, dpi: number): number {
  return cm360ToSens(cm360, dpi, OUR_YAW);
}

const deg = (r: number): number => (r * 180) / Math.PI;
const rad = (d: number): number => (d * Math.PI) / 180;

/** The source game's labelled FOV -> vertical FOV. */
export function fovToVfov(fov: number, type: SensFovType | undefined): number {
  const t = Math.tan(rad(fov / 2));
  if (type === "h4:3") return deg(2 * Math.atan((3 / 4) * t));
  if (type === "h") return deg(2 * Math.atan((9 / 16) * t));
  return fov;
}

/** Vertical FOV -> the FOV value the game's UI would show for `type`. */
export function vfovToGameFov(vfov: number, type: SensFovType | undefined): number {
  const t = Math.tan(rad(vfov / 2));
  if (type === "h4:3") return deg(2 * Math.atan((4 / 3) * t));
  if (type === "h") return deg(2 * Math.atan((16 / 9) * t));
  return vfov;
}

/** Vertical FOV -> horizontal FOV for an aspect ratio (w/h). */
export function vfovToHfov(vfov: number, aspect: number): number {
  return deg(2 * Math.atan(aspect * Math.tan(rad(vfov / 2))));
}

/**
 * webrace FOV that shows the same vertical extent as the source game's `fov`
 * (same apparent FOV height; our cvar is the horizontal FOV for the current
 * aspect).
 */
export function ourFov(
  sourceFov: number,
  type: SensFovType | undefined,
  aspect: number,
): number {
  return vfovToHfov(fovToVfov(sourceFov, type), aspect);
}

/** The FOV value a game's profile labels as its default, if known. */
export function defaultGameFov(
  vfov: number | undefined,
  hfov: number | undefined,
  type: SensFovType | undefined,
): number | null {
  if (vfov) return vfovToGameFov(vfov, type);
  return hfov ?? null;
}
